import { NotificationQueue, NotificationTemplate, NotificationSettings } from '../types';
import emailjs from '@emailjs/browser';

export const createNotification = async (
  bookingId: number,
  customerId: number,
  type: 'email' | 'sms',
  category: 'confirmation' | 'reminder_24h' | 'reminder_2h' | 'cancelation',
  recipient: string,
  templateData: any
): Promise<NotificationQueue> => {
  const notification: NotificationQueue = {
    id: Date.now(),
    booking_id: bookingId,
    customer_id: customerId,
    type,
    category,
    recipient,
    content: '',
    scheduledFor: new Date().toISOString(),
    status: 'pending',
    attempts: 0
  };

  // Template-Inhalt generieren
  notification.content = generateNotificationContent(category, templateData);
  
  if (type === 'email') {
    notification.subject = generateEmailSubject(category, templateData);
  }

  return notification;
};

export const sendRealEmail = async (notification: NotificationQueue): Promise<boolean> => {
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  if (!serviceId || !templateId || !publicKey) {
    console.warn('EmailJS nicht konfiguriert — Keys fehlen in .env.local');
    return false;
  }

  try {
    await emailjs.send(serviceId, templateId, {
      to_email: notification.recipient,
      to_name: 'Kunde',
      message: notification.content,
      subject: notification.subject || 'Terminbestätigung',
    }, publicKey);
    return true;
  } catch (error) {
    console.error('E-Mail-Versand fehlgeschlagen:', error);
    return false;
  }
};

export const sendNotification = async (notification: NotificationQueue): Promise<boolean> => {
  try {
    notification.attempts += 1;
    notification.lastAttempt = new Date().toISOString();

    let success = false;

    if (notification.type === 'email') {
      success = await sendRealEmail(notification);
    } else if (notification.type === 'sms') {
      success = await sendSMS(notification);
    }

    if (success) {
      notification.status = 'sent';
      notification.sentAt = new Date().toISOString();
    } else {
      notification.status = 'failed';
      notification.errorMessage = 'Versand fehlgeschlagen';
    }

    return success;
  } catch (error) {
    notification.status = 'failed';
    notification.errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
    return false;
  }
};

const sendSMS = async (_notification: NotificationQueue): Promise<boolean> => {
  console.warn('SMS-Versand nicht konfiguriert — Twilio-Keys fehlen in .env.local');
  return false;
};

const generateNotificationContent = (category: string, data: any): string => {
  const templates = {
    confirmation: `Hallo ${data.customerName},

vielen Dank für Ihre Buchung bei TraumBeauty Cakar.

Termin: ${data.date} um ${data.time}
Service: ${data.serviceName}
Standort: ${data.locationName}

Wir freuen uns auf Sie!

Mit freundlichen Grüßen
TraumBeauty Cakar Team`,

    reminder_24h: `Hallo ${data.customerName},

wir erinnern Sie an Ihren morgigen Termin:

Termin: ${data.date} um ${data.time}
Service: ${data.serviceName}
Standort: ${data.locationName}

Bitte kommen Sie 10 Minuten vor dem Termin.

Mit freundlichen Grüßen
TraumBeauty Cakar Team`,

    reminder_2h: `Hallo ${data.customerName},

Ihr Termin beginnt in 2 Stunden:

Termin: ${data.date} um ${data.time}
Service: ${data.serviceName}
Standort: ${data.locationName}

Wir freuen uns auf Sie!

Mit freundlichen Grüßen
TraumBeauty Cakar Team`,

    cancelation: `Hallo ${data.customerName},

Ihr Termin wurde erfolgreich storniert:

Termin: ${data.date} um ${data.time}
Service: ${data.serviceName}

Bei Fragen kontaktieren Sie uns gerne.

Mit freundlichen Grüßen
TraumBeauty Cakar Team`
  };

  return templates[category as keyof typeof templates] || 'Benachrichtigung';
};

const generateEmailSubject = (category: string, data: any): string => {
  const subjects = {
    confirmation: 'Terminbestätigung - TraumBeauty Cakar',
    reminder_24h: 'Erinnerung: Ihr Termin morgen - TraumBeauty Cakar',
    reminder_2h: 'Ihr Termin beginnt in 2 Stunden - TraumBeauty Cakar',
    cancelation: 'Terminstornierung - TraumBeauty Cakar'
  };

  return subjects[category as keyof typeof subjects] || 'TraumBeauty Cakar';
};

export const scheduleBookingNotifications = async (booking: any): Promise<void> => {
  // Feldnamen normalisieren: Supabase-Antwort hat camelCase + snake_case Varianten
  const customerEmail =
    booking.customerEmail ??
    booking.customer?.email ??
    booking.customer_email ?? '';
  const customerName =
    booking.customerName ??
    booking.customer?.name ??
    booking.customer_name ?? 'Kunde';
  const bookingDate = booking.bookingDate ?? booking.booking_date ?? booking.date ?? '';
  const bookingTime = booking.bookingTime ?? booking.booking_time ?? booking.time ?? '';
  const serviceName = booking.service?.name ?? booking.serviceName ?? '';
  const locationName = booking.location?.name ?? booking.locationName ?? '';

  if (!customerEmail) {
    console.warn('scheduleBookingNotifications: keine Kunden-E-Mail — überspringe Benachrichtigung');
    return;
  }

  const templateData = { customerName, date: bookingDate, time: bookingTime, serviceName, locationName };
  const notifications: NotificationQueue[] = [];

  // Bestätigungs-E-Mail sofort senden
  const confirmationNotification = await createNotification(
    booking.id,
    booking.customerId ?? booking.customer_id ?? 0,
    'email',
    'confirmation',
    customerEmail,
    templateData
  );

  // Sofort versenden (nicht nur in Queue stellen)
  await sendNotification(confirmationNotification);
  notifications.push(confirmationNotification);

  // Reminder werden nur in die Queue gestellt — kein echter Cron-Job vorhanden.
  // Sobald Supabase Edge Functions + pg_cron konfiguriert sind, hier senden.
  const customerId = booking.customerId ?? booking.customer_id ?? 0;

  const reminder24hDate = new Date(bookingDate + 'T09:00');
  reminder24hDate.setDate(reminder24hDate.getDate() - 1);
  const reminder24h = await createNotification(booking.id, customerId, 'email', 'reminder_24h', customerEmail, templateData);
  reminder24h.scheduledFor = reminder24hDate.toISOString();
  notifications.push(reminder24h);

  const reminder2hDate = new Date(bookingDate + 'T' + bookingTime);
  reminder2hDate.setHours(reminder2hDate.getHours() - 2);
  const reminder2h = await createNotification(booking.id, customerId, 'email', 'reminder_2h', customerEmail, templateData);
  reminder2h.scheduledFor = reminder2hDate.toISOString();
  notifications.push(reminder2h);

  // Reminder in Supabase-Tabelle speichern, wenn vorhanden
  try {
    const { supabase } = await import('../lib/supabase');
    await supabase.from('notification_queue').insert(
      notifications.slice(1).map(n => ({
        booking_id: n.booking_id,
        customer_id: n.customer_id,
        type: n.type,
        category: n.category,
        recipient: n.recipient,
        content: n.content,
        subject: n.subject,
        scheduled_for: n.scheduledFor,
        status: 'pending',
        attempts: 0,
      }))
    );
  } catch {
    // Tabelle existiert möglicherweise noch nicht — kein harter Fehler
  }
};